import React from "react"
import { observer } from "mobx-react-lite"
import { useNavigate } from "react-router-dom"
import { useAlert } from "react-alert"

import TooltipContainer from "@components/ui/Tooltip/TooltipContainer"
import Tooltip from "@components/ui/Tooltip/Tooltip"
import useToggle from "@components/hooks/useToggle"
import Icon from "@components/ui/Icon/Icon"
import MenuItem from "@components/containers/Sidebar/MenuItem"
import { ContextOption } from "@framework/types/common"
import IconButton from "@components/ui/IconButton/IconButton"
import { useController, useStore } from "@store"
import { V2_Products } from "@store/product/product.store"
import mainRoutes from "@root/main.routes"

type ContextMenuProps = {
  product: V2_Products
  onDelete: () => void
}

const ContextMenu: React.FC<ContextMenuProps> = observer(
  ({ product, onDelete }) => {
    const { isOpened, handleToggle, setOpened } = useToggle()
    const { restrictionsStore: access } = useStore()
    const navigate = useNavigate()
    const { productController } = useController()
    const alert = useAlert()

    const contextOptions: ContextOption<string>[] = []

    if (access.canUpdateProduct) {
      contextOptions.push({
        label: "Edit",
        icon: "edit",
        type: "Edit",
        onClick: () => {
          navigate(mainRoutes.productEdit(product.id))
        },
      })
    }

    if (access.canDeleteProduct) {
      contextOptions.push({
        label: "Delete",
        icon: "trash-can",
        color: "red",
        type: "Delete",
        onClick: async () => {
          const success = await productController.deleteProduct(product.id)
          if (success) {
            alert.success(`Successfully deleted ${product.name}`)
            onDelete()
          } else {
            alert.error(`Failed to delete ${product.name}`)
          }
        },
      })
    }

    return (
      <Tooltip
        show={isOpened}
        onClose={() => setOpened(false)}
        mode="onFocus"
        content={
          <TooltipContainer color="primary" placement="left">
            {contextOptions.map(({ label, type, ...rest }) => (
              <MenuItem key={type} onClick={() => setOpened(false)} {...rest}>
                {label}
              </MenuItem>
            ))}
          </TooltipContainer>
        }
      >
        <IconButton size="medium" active={isOpened} onClick={handleToggle}>
          <Icon name="dots" />
        </IconButton>
      </Tooltip>
    )
  }
)

export default ContextMenu
